import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { AppRoute, QueryReference } from '@agentselly/immodossier-shared';
import { createFileRoute } from '@tanstack/react-router';
import { useBackendQuery } from '~/helper/useBackendQuery';
import { useTranslate } from '@tolgee/react';
import { LoadingWidget } from '~/components/widget/base/LoadingWidget';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import type { PagePublicCallSessionData } from '@agentselly/immodossier-shared/dist/query/model/PagePublicCallSessionData';
import type { CallStatistics } from '@agentselly/immodossier-shared/dist/query/model/PagePublicCallSessionData';
import { ActionButton } from '~/components/button/ActionButton';

import { Calendar3 } from '~/generated/svg/icon/Calendar3';
import { CallNotReached } from '~/generated/svg/icon/CallNotReached';
import { CallArchieved } from '~/generated/svg/icon/CallArchieved';
import { Minimize } from '~/generated/svg/icon/Minimize';
import { Maximize } from '~/generated/svg/icon/Maximize';
import { Crown } from '~/generated/svg/icon/Crown';
import { Trophy } from '~/generated/svg/icon/Trophy';
import { ArrowRight } from '~/generated/svg/ArrowRight';

export const Route = createFileRoute(
  AppRoute.$LANG_$COMPANYID_PUBLIC_SALES_CALL_SESSION,
)({
  component: Body,
});

function Body() {
  const { t } = useTranslate();
  const now = new Date();
  const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, ...
  const currentHour = now.getHours();

  // Enable auto-refresh only on Mondays (1) and Wednesdays (3) during business hours (8:00-18:00)
  // as these are the main calling days when the dashboard is displayed on screens
  const isCallSessionDay = currentDay === 1 || currentDay === 3;
  const isDuringBusinessHours = currentHour >= 8 && currentHour < 18;

  const query = useBackendQuery(QueryReference.PagePublicCallSession, {
    refetchInterval:
      isCallSessionDay && isDuringBusinessHours ? 60000 : undefined,
  });

  const [
    agents,
    setAgents,
  ] = useState<PagePublicCallSessionData['salesAgents']>([]);
  const [
    fullscreen,
    setFullscreen,
  ] = useState(true);

  useEffect(() => {
    if (query.isSuccess) {
      setAgents(query.data?.salesAgents || []);
    }
  }, [query]);

  if (query.isLoading) {
    return <LoadingWidget />;
  }

  if (query.isError) {
    return <ErrorWidget error={query.error} />;
  }

  const toggleFullscreen = () => {
    setFullscreen(!fullscreen);
  };

  return (
    <div
      className={clsx(
        `wrapper`,
        fullscreen
          ? 'fixed inset-0 z-50 flex flex-col items-center justify-center bg-white dark:bg-gray-800'
          : '',
      )}
    >
      <style>{shimmerStyle}</style>
      <div
        className={clsx(
          'h-full bg-white dark:bg-gray-800 p-3 md:p-8',
          fullscreen ? '' : 'drop-shadow-md rounded-xl',
        )}
      >
        <div className="flex justify-between items-center mb-2">
          <div>
            <h1 className="text-xl font-bold">
              {t('public_sales_call_session_title')}
            </h1>
            <span className="text-xs">
              {t('public_sales_call_session_last_update', {
                date: new Date(query.data?.lastUpdate || '').toLocaleString(
                  'de-CH',
                  {
                    hour: '2-digit',
                    minute: '2-digit',
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  },
                ),
              })}
            </span>
          </div>

          <ActionButton onClick={toggleFullscreen}>
            {fullscreen ? (
              <Minimize className="h-5 w-5" />
            ) : (
              <Maximize className="h-5 w-5" />
            )}
          </ActionButton>
        </div>
        <div className="flex flex-col items-center justify-center h-[85%]">
          <div
            className={clsx(
              'grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 md:p-0 md:py-2 items-center',
              agents.length === 5
                ? 'xl:grid-cols-5'
                : agents.length === 4
                ? 'xl:grid-cols-4'
                : 'xl:grid-cols-6',
            )}
          >
            {agents.map((agent) => (
              <div
                key={agent.id}
                className={clsx(
                  'rounded-xl border shadow-sm dark:shadow-gray-900/50 flex flex-col',
                  agent.statistics.score === 0
                    ? 'bg-red-50 dark:bg-red-950/30'
                    : 'bg-white dark:bg-gray-800',
                  fullscreen ? 'shadow-lg dark:shadow-gray-900/50' : '',
                  agent.rank === 1
                    ? 'border-2 border-yellow-400' // Gold
                    : agent.rank === 2
                    ? 'border-2 border-gray-400' // Silver
                    : agent.rank === 3
                    ? 'border-2 border-yellow-700' // Bronze
                    : 'border border-gray-100 dark:border-gray-700',
                )}
              >
                {agent.rank === 1 && (
                  <div className="relative">
                    <Crown className="h-6 w-6 absolute top-0 left-1/2 transform -translate-x-1/2 text-yellow-400 shimmer" />
                  </div>
                )}
                <div className="items-center text-center mt-1">
                  {agent.statistics.score > 0 ? (
                    <div
                      className={clsx(
                        `text-base`,
                        agent.rank === 1
                          ? 'text-yellow-400'
                          : agent.rank === 2
                          ? 'text-gray-400'
                          : 'text-yellow-400',
                      )}
                    >
                      {agent.rank}
                    </div>
                  ) : (
                    <div className="text-base text-gray-500 dark:text-gray-400"></div>
                  )}
                </div>
                <div className="px-3 pb-3 flex flex-col gap-1 items-center text-center">
                  <h2 className="font-bold text-sm">
                    {agent.firstname} {agent.lastname}
                  </h2>
                  <div className="flex items-center justify-center">
                    <span className="text-lg font-extrabold">
                      {agent.statistics.score}
                    </span>
                    <Trophy className="h-4 w-4 ml-1" />
                  </div>
                  {/* Weekly stats */}
                  <div className="text-[9px] font-semibold text-gray-400 dark:text-gray-500 dark:text-gray-400 uppercase tracking-wider mt-1">
                    Woche
                  </div>
                  <div className="text-left space-y-1 w-full">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <ArrowRight className="h-3.5 w-3.5 text-blue-600" />
                        <span className="text-[9px] ml-1">
                          {t('public_sales_call_session_statistics_referred')}
                        </span>
                      </div>
                      <span className="text-sm ml-2 text-blue-600 font-bold">
                        {agent.statistics.weekly.referred}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Calendar3 className="h-3.5 w-3.5" />
                        <span className="text-[9px] ml-1">
                          {t('public_sales_call_session_statistics_terminated')}
                        </span>
                      </div>
                      <span className="text-sm ml-2 font-bold">
                        {agent.statistics.weekly.terminated}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <CallArchieved className="h-3.5 w-3.5 text-green-500" />
                        <span className="text-[9px] ml-1">
                          {t('public_sales_call_session_statistics_achieved')}
                        </span>
                      </div>
                      <span className="text-sm text-green-500 ml-2 font-bold">
                        {agent.statistics.weekly.achieved}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <CallNotReached className="h-3.5 w-3.5 text-red-500" />
                        <span className="text-[9px] ml-1">
                          {t('public_sales_call_session_statistics_notReached')}
                        </span>
                      </div>
                      <span className="text-sm text-red-500 ml-2 font-bold">
                        {agent.statistics.weekly.notReached}
                      </span>
                    </div>
                  </div>
                  {/* Divider */}
                  <div className="w-full border-t border-gray-200 dark:border-gray-700 my-0.5" />
                  {/* Today stats */}
                  <div className="text-[9px] font-semibold text-gray-400 dark:text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Heute
                  </div>
                  <div className="text-left space-y-1 w-full">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <ArrowRight className="h-3.5 w-3.5 text-blue-600 opacity-60" />
                        <span className="text-[9px] ml-1 text-gray-500 dark:text-gray-400">
                          {t('public_sales_call_session_statistics_referred')}
                        </span>
                      </div>
                      <span className="text-xs ml-2 text-blue-600 opacity-80 font-bold">
                        {agent.statistics.today.referred}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Calendar3 className="h-3.5 w-3.5 opacity-60" />
                        <span className="text-[9px] ml-1 text-gray-500 dark:text-gray-400">
                          {t('public_sales_call_session_statistics_terminated')}
                        </span>
                      </div>
                      <span className="text-xs ml-2 text-gray-600 dark:text-gray-400 opacity-80 font-bold">
                        {agent.statistics.today.terminated}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <CallArchieved className="h-3.5 w-3.5 text-green-500 opacity-60" />
                        <span className="text-[9px] ml-1 text-gray-500 dark:text-gray-400">
                          {t('public_sales_call_session_statistics_achieved')}
                        </span>
                      </div>
                      <span className="text-xs text-green-500 ml-2 opacity-80 font-bold">
                        {agent.statistics.today.achieved}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <CallNotReached className="h-3.5 w-3.5 text-red-500 opacity-60" />
                        <span className="text-[9px] ml-1 text-gray-500 dark:text-gray-400">
                          {t('public_sales_call_session_statistics_notReached')}
                        </span>
                      </div>
                      <span className="text-xs text-red-500 ml-2 opacity-80 font-bold">
                        {agent.statistics.today.notReached}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div
        className={clsx(
          'mt-8 p-4 border-t border-gray-200 dark:border-gray-700 flex justify-center text-gray-500 dark:text-gray-400',
          fullscreen ? 'fixed bottom-0 mx-auto w-full' : '',
        )}
      >
        <ul className="flex space-x-4 text-sm">
          <li>{t('public_sales_call_session_statistics_referred')} = 15x</li>
          <li>{t('public_sales_call_session_statistics_terminated')} = 15x</li>
          <li>{t('public_sales_call_session_statistics_achieved')} = 5x</li>
          <li>{t('public_sales_call_session_statistics_notReached')} = 1x</li>
        </ul>
      </div>
    </div>
  );
}

const shimmerStyle = `
  @keyframes shimmer {
    100% {
      -webkit-mask-position: left;
    }
  }
  .shimmer {
    color: rgb(250, 204, 21);
    display: inline-block;
    mask: linear-gradient(-60deg, #000 30%, #0005, #000 70%) right/300% 100%;
    -webkit-mask: linear-gradient(-60deg, #000 30%, #0005, #000 70%) right/300% 100%;
    background-repeat: no-repeat;
    animation: shimmer 2s infinite;
  }
`;
